import api from './api';
import { ApiResponse } from '../types';

// Interface para dados do modal de pagamento (resposta da rota get-payment-invoice-data)
export interface PaymentInvoiceDataResponse {
  invoice: {
    id: number;
    cliente_id: number;
    valor: number;
    acrescimo: number;
    desconto: number;
    status: string;
    descricao: string;
    data_vencimento: string;
    data_emissao?: string;
    valor_multa: number;
    valor_juros: number;
    dias_atraso: number;
  };
  paymentSummary: PaymentSummary;
  availableCredits: PaymentInvoiceCredit[];
  descontoAntecipadoConfig?: {
    tipo: 'PERCENTAGE' | 'FIXED' | '';
    valor: number;
    dias: number;
  };
}

// Interface para crédito na resposta do modal
export interface PaymentInvoiceCredit {
  id: number;
  cliente_id: number;
  tipo: string;
  valor: number;
  saldo: number;
  cliente_origem_id?: number;
  observacoes?: string;
  createdAt: string;
  updatedAt: string;
  cliente_origem?: { id: number; nome: string };
}

// Interface para pagamento de fatura
export interface InvoicePayment {
  id: number;
  fatura_id: number;
  valor_pago: number;
  valor_original?: number;
  acrescimo?: number;
  desconto?: number;
  motivo_desconto_id?: number;
  motivo_desconto?: string | null;
  forma_pagamento: string;
  data_pagamento: string;
  transacao_id?: string;
  observacoes?: string;
  tipo_pagamento: 'normal' | 'excedente_credito';
  usuarioId_coletor?: number | null;
  coletor?: { id: number; nome: string } | null;
  createdAt: string;
  updatedAt: string;
  motivoDesconto?: { id: number; descricao: string };
}

// Interface para dados de pagamento
export interface PaymentSummary {
  totalAmount: number;       // Valor total da fatura
  totalPaid: number;         // Valor total já pago
  remainingAmount: number;   // Valor restante a pagar
  payments: InvoicePayment[]; // Lista de pagamentos
  isPaid: boolean;           // Se está completamente pago
  isPartiallyPaid: boolean;  // Se está parcialmente pago
}

class PaymentService {
  // Buscar pagamentos de uma fatura
  async getPaymentsByInvoice(invoiceId: number): Promise<ApiResponse<InvoicePayment[]>> {
    try {
      const response = await api.get(`/invoice-payments/invoice/${invoiceId}`);
      
      // O interceptor do axios retorna response.data, que já é o objeto ApiResponse do backend
      if (response && typeof response === 'object' && 'success' in response) {
        return response as ApiResponse<InvoicePayment[]>;
      }
      
      // Se é um array diretamente, normalizar para ApiResponse
      if (Array.isArray(response)) {
        return {
          success: true,
          data: response as InvoicePayment[],
          message: 'Pagamentos carregados com sucesso'
        };
      }
      
      throw new Error('Formato de resposta inválido da API');
    } catch (error) {
      console.error('Erro ao buscar pagamentos da fatura:', error);
      throw error;
    }
  }

  // Calcular resumo de pagamentos de uma fatura
  async getPaymentSummary(
    invoiceId: number, 
    invoiceValue: number, 
    invoiceIncrease: number = 0, 
    invoiceDiscount: number = 0
  ): Promise<PaymentSummary> {
    try {
      // Calcular valor total da fatura
      const totalAmount = invoiceValue + invoiceIncrease - invoiceDiscount;
      
      // Buscar pagamentos
      const paymentsResponse = await this.getPaymentsByInvoice(invoiceId);
      const payments = paymentsResponse.data || [];
      
      // Calcular total pago
      const totalPaid = payments.reduce((sum, payment) => sum + Number(payment.valor_pago), 0);
      
      // Calcular valor restante
      const remainingAmount = totalAmount - totalPaid;
      
      return {
        totalAmount,
        totalPaid,
        remainingAmount: remainingAmount > 0 ? remainingAmount : 0,
        payments,
        isPaid: remainingAmount <= 0,
        isPartiallyPaid: totalPaid > 0 && remainingAmount > 0
      };
    } catch (error) {
      console.error('Erro ao calcular resumo de pagamentos:', error);
      
      // Retornar resumo básico em caso de erro
      const totalAmount = invoiceValue + invoiceIncrease - invoiceDiscount;
      return {
        totalAmount,
        totalPaid: 0,
        remainingAmount: totalAmount,
        payments: [],
        isPaid: false,
        isPartiallyPaid: false
      };
    }
  }

  /**
   * Buscar dados do modal de pagamento em uma única chamada.
   * Retorna: fatura (dados mínimos), paymentSummary e créditos disponíveis.
   */
  async getPaymentInvoiceData(invoiceId: number): Promise<ApiResponse<PaymentInvoiceDataResponse>> {
    try {
      const response = await api.get(`/invoice-payments/get-payment-invoice-data/${invoiceId}`);

      if (response && typeof response === 'object' && 'success' in response) {
        return response as ApiResponse<PaymentInvoiceDataResponse>;
      }

      throw new Error('Formato de resposta inválido da API');
    } catch (error) {
      console.error('Erro ao buscar dados do modal de pagamento:', error);
      throw error;
    }
  }
}

export default new PaymentService(); 